import { useState } from 'react';
import type { ObjectiveId } from '@contracts';
import { useDomainApi } from '@ui/shared/domain/DomainApiContext';
import { FooterTextButton, LifecycleConfirmDialog, LifecycleFooterShell } from '@ui/shared/primitives/LifecycleFooter';

/**
 * Archive footer action (objectives.md v2.0 §7, MC-1/MC-2/MC-3) —
 * `EditObjectiveScreen`'s "Archive objective" text action, an inline (not
 * modal) confirmation, and a one-line post-archive receipt. Shares its
 * chrome (the text action, the `role="alertdialog"` inline confirmation —
 * no `window.confirm`, no modal, no scrim) with `HabitFormScreen.tsx`'s
 * habit lifecycle footer via `@ui/shared/primitives/LifecycleFooter`.
 * No "Pause" here — objectives do have an `on_hold` state (Phase 5), but its
 * control lives separately on `EditObjectiveScreen`'s own Status field
 * (`StatusSegmentedControl`, active/on_hold), matching desktop's own
 * separation of that control from its archive footer. This footer stays
 * archive-only: a genuinely different, one-way lifecycle transition.
 */
export function ObjectiveLifecycleFooter({
  objectiveId,
  title,
  alreadyArchived,
  onArchived,
}: {
  readonly objectiveId: ObjectiveId;
  readonly title: string;
  /**
   * True once `getObjective`'s own re-fetch reflects `state: 'archived'`
   * (a real, persisted fact) — distinct from this component's own local
   * `justArchived` (the "I just clicked Archive, show the receipt" moment).
   * Kept separate so a genuinely-archived-on-arrival objective (e.g. direct
   * navigation to an already-archived one, no local click ever happened)
   * still renders the quiet "Archived" state rather than the active footer.
   */
  readonly alreadyArchived: boolean;
  readonly onArchived: () => void;
}) {
  const api = useDomainApi();
  const [confirming, setConfirming] = useState(false);
  const [justArchived, setJustArchived] = useState(false);

  if (justArchived || alreadyArchived) {
    return (
      <LifecycleFooterShell>
        <p style={{ fontSize: 'var(--type-body-size)', color: 'var(--text-secondary)', margin: 0 }}>
          Archived — its history stays in your completion view.
        </p>
      </LifecycleFooterShell>
    );
  }

  return (
    <LifecycleFooterShell>
      <div style={{ display: 'flex', gap: 'var(--space-4)', flexWrap: 'wrap' }}>
        <FooterTextButton onClick={() => setConfirming(true)} label="Archive objective" />
      </div>
      <p style={{ fontSize: 'var(--type-caption-size)', color: 'var(--text-muted)', marginTop: 'var(--space-2)' }}>
        Objectives archive rather than delete, so your history stays honest.
      </p>

      {confirming && (
        <LifecycleConfirmDialog
          ariaLabel={`Archive ${title}`}
          message={
            <>
              Archive &quot;{title}&quot;? It moves out of active views. Every action, forecast, and adjustment stays in
              your history and your export.
            </>
          }
          confirmLabel="Archive"
          onConfirm={() => {
            void api.archiveObjective(objectiveId).then(() => {
              setConfirming(false);
              setJustArchived(true);
              // §7.3: "the receipt displays briefly and then the screen
              // returns to Today" — a short, visible pause before
              // navigating away, not an instant redirect that would
              // never let the receipt actually be read.
              window.setTimeout(onArchived, 1400);
            });
          }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </LifecycleFooterShell>
  );
}
